/**
 * Serviço de integração com Mercado Pago
 * Documentação: https://www.mercadopago.com.br/developers
 */

import { supabase } from '../supabaseClient';
import { isSaleablePlan } from '../config/paymentCatalog';

function configuredPublicKey() {
  const environment = getExpectedPaymentEnvironment();
  if (environment === 'test') return process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY_TEST;
  return process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY_PRODUCTION
    || process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;
}

const MERCADOPAGO_PUBLIC_KEY = configuredPublicKey();
const PAYMENT_REQUEST_TIMEOUT_MS = 20000;
let mercadoPagoSdkPromise = null;

async function parseFunctionResponse(response, fallbackMessage) {
  const raw = await response.text();
  let payload = {};
  try {
    payload = raw ? JSON.parse(raw) : {};
  } catch (_) {
    throw new Error(fallbackMessage);
  }
  if (!response.ok) {
    const error = new Error(payload.error || fallbackMessage);
    error.status = response.status;
    error.retryable = Boolean(payload.retryable) || response.status === 409 || response.status >= 500;
    throw error;
  }
  return payload;
}

async function callPaymentFunction(functionName, accessToken, body, fallbackMessage) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), PAYMENT_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await parseFunctionResponse(response, fallbackMessage);
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('O serviço de pagamento demorou mais que o esperado. Tente novamente.');
    }
    if (error instanceof TypeError && /failed to fetch|networkerror|load failed|network request failed/i.test(error.message)) {
      throw new Error('Não foi possível conectar ao serviço de cobrança. Recarregue a página e tente novamente. Se o erro continuar, entre em contato com o suporte.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function getExpectedPaymentEnvironment() {
  const configured = String(process.env.REACT_APP_MERCADOPAGO_ENVIRONMENT || 'test').toLowerCase();
  return configured === 'production' ? 'production' : 'test';
}

export function getPublicKeyEnvironment(publicKey = MERCADOPAGO_PUBLIC_KEY) {
  if (publicKey && publicKey === process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY_TEST) return 'test';
  if (publicKey && publicKey === process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY_PRODUCTION) return 'production';
  if (String(publicKey || '').startsWith('TEST-')) return 'test';
  if (String(publicKey || '').startsWith('APP_USR-')) return 'production';
  return 'unknown';
}

export function assertMercadoPagoPublicKeyEnvironment(publicKey = MERCADOPAGO_PUBLIC_KEY) {
  const expected = getExpectedPaymentEnvironment();
  const detected = getPublicKeyEnvironment(publicKey);
  if (expected === 'test'
      && !process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY_TEST
      && !String(publicKey || '').startsWith('TEST-')) {
    throw new Error('Pagamento bloqueado por segurança: a chave pública exclusiva de homologação não foi configurada.');
  }
  if (detected !== expected) {
    throw new Error(
      `Pagamento bloqueado por segurança: a chave pública é de ${detected === 'production' ? 'produção' : detected === 'test' ? 'teste' : 'origem desconhecida'}, ` +
      `mas o aplicativo está configurado para ${expected === 'production' ? 'produção' : 'teste'}.`
    );
  }
}

function createCheckoutAttemptId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const MercadoPagoService = {
  /**
   * Inicializa o Mercado Pago
   */
  inicializar() {
    // Verifica se a chave está configurada
    if (!MERCADOPAGO_PUBLIC_KEY) {
      console.error('MERCADOPAGO_PUBLIC_KEY não está configurada');
      return Promise.reject(new Error('Chave do Mercado Pago não configurada'));
    }
    try {
      assertMercadoPagoPublicKeyEnvironment();
    } catch (error) {
      console.error(error.message);
      return Promise.reject(error);
    }

    // A validação vem antes do cache para impedir reutilização de uma SDK
    // inicializada em outro ambiente durante hot reload ou troca de configuração.
    if (window.mp) return Promise.resolve(window.mp);
    if (window.MercadoPago) {
      window.mp = new window.MercadoPago(MERCADOPAGO_PUBLIC_KEY, { locale: 'pt-BR' });
      return Promise.resolve(window.mp);
    }
    if (mercadoPagoSdkPromise) return mercadoPagoSdkPromise;

    mercadoPagoSdkPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-climorax-mercadopago-sdk="true"]');
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.dataset.climoraxMercadopagoSdk = 'true';
      
      script.onload = () => {
        try {
          if (window.MercadoPago) {
            window.mp = new window.MercadoPago(MERCADOPAGO_PUBLIC_KEY, {
              locale: 'pt-BR'
            });
            console.log('✅ Mercado Pago inicializado com sucesso');
            resolve(window.mp);
          } else {
            reject(new Error('SDK do Mercado Pago não carregou corretamente'));
          }
        } catch (error) {
          console.error('❌ Erro ao inicializar Mercado Pago:', error);
          reject(error);
        }
      };
      
      script.onerror = () => {
        reject(new Error('Falha ao carregar SDK do Mercado Pago'));
      };
      
      if (existingScript) {
        existingScript.addEventListener('load', script.onload, { once: true });
        existingScript.addEventListener('error', script.onerror, { once: true });
      } else {
        document.body.appendChild(script);
      }
    });
    mercadoPagoSdkPromise.catch(() => {
      mercadoPagoSdkPromise = null;
    });
    return mercadoPagoSdkPromise;
  },

  /**
   * Cria uma preferência de pagamento
   * @param {Object} params - Parâmetros do pagamento
   * @param {string} params.plano - 'essential', 'professional' ou 'management'
   * @param {string} params.periodicidade - 'mensal' ou 'anual'
   * @param {Object} params.usuario - Dados do usuário
   */
  async criarAssinatura({ plano, periodicidade, usuario }) {
    if (!usuario?.id) throw new Error('Usuário não autenticado');
    if (!isSaleablePlan(plano)) throw new Error('Este produto não está disponível para novas assinaturas.');
    if (!['mensal', 'anual'].includes(periodicidade)) throw new Error('Periodicidade inválida.');

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) throw new Error('Sua sessão expirou. Entre novamente para continuar.');

    try {
      const attemptKey = `climorax:checkout-attempt:${plano}:${periodicidade}`;
      const checkoutAttemptId = window.sessionStorage.getItem(attemptKey) || createCheckoutAttemptId();
      window.sessionStorage.setItem(attemptKey, checkoutAttemptId);
      console.log('Criando assinatura recorrente...', { plano, periodicidade });
      
      // Chama sua Edge Function no Supabase para criar a preferência
      const data = await callPaymentFunction(
        'criar-assinatura-mp-v2',
        accessToken,
        { plano, periodicidade, checkoutAttemptId },
        'Não foi possível preparar a assinatura.',
      );
      const expectedEnvironment = getExpectedPaymentEnvironment();
      if (data.environment !== expectedEnvironment) {
        throw new Error(
          `Checkout bloqueado: o aplicativo está em ${expectedEnvironment === 'test' ? 'teste' : 'produção'}, ` +
          `mas o serviço de pagamento respondeu em ${data.environment || 'ambiente não identificado'}.`
        );
      }
      console.log('Assinatura criada:', data.id);
      window.sessionStorage.removeItem(attemptKey);
      return data;
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      throw error;
    }
  },

  /**
   * Abre o checkout do Mercado Pago
   */
  async abrirCheckout({ plano, periodicidade, usuario }) {
    try {
      console.log('🚀 Abrindo checkout...', { plano, periodicidade });
      
      const assinatura = await this.criarAssinatura({ plano, periodicidade, usuario });
      
      if (!assinatura || !assinatura.init_point) {
        throw new Error('Assinatura inválida - link de autorização não encontrado');
      }
      
      window.sessionStorage.setItem('climorax:pending-subscription-id', assinatura.id);
      window.sessionStorage.setItem('climorax:pending-subscription-environment', assinatura.environment);
      console.log('Redirecionando para autorização da assinatura:', assinatura.init_point);
      
      const checkoutUrl = new URL(assinatura.init_point);
      if (checkoutUrl.protocol !== 'https:' || !/(^|\.)mercadopago\.com(\.br)?$/i.test(checkoutUrl.hostname)) {
        throw new Error('O endereço de checkout recebido não pertence ao Mercado Pago.');
      }
      window.location.assign(checkoutUrl.toString());
      
      return assinatura;
    } catch (error) {
      console.error('❌ Erro ao abrir checkout:', error);
      throw error;
    }
  },

  /**
   * Verifica status de um pagamento
   */
  async consultarAssinatura(subscriptionId) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Sua sessão expirou. Entre novamente para continuar.');

      const data = await callPaymentFunction(
        'consultar-assinatura-mp-v2',
        accessToken,
        { subscriptionId },
        'Não foi possível consultar a assinatura.',
      );
      if (data.environment !== getExpectedPaymentEnvironment()) {
        throw new Error('A assinatura pertence a um ambiente de pagamento diferente deste aplicativo.');
      }
      return data;
    } catch (error) {
      console.error('Erro ao consultar assinatura:', error);
      throw error;
    }
  },

  async gerenciarAssinatura(subscriptionId, acao) {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) throw new Error('Sua sessão expirou. Entre novamente para continuar.');

    const data = await callPaymentFunction(
      'gerenciar-assinatura-mp-v2',
      accessToken,
      { subscriptionId, acao },
      'Não foi possível atualizar a assinatura.',
    );
    if (data.environment !== getExpectedPaymentEnvironment()) {
      throw new Error('A assinatura pertence a outro ambiente de pagamento.');
    }
    return data;
  }
};
